/**
 * The CenoX AI Assistant.
 *
 * Two things this screen does that a plain chat box would not, both because
 * the customer is paying per token:
 *
 *  - **it shows the price before the question.** The tier selector names what
 *    each tier costs per 100,000 tokens, and a tier the plan does not include
 *    is visible and disabled rather than hidden. Hiding it would mean a
 *    customer never learns the capability exists; showing it enabled would
 *    mean they discover the limit by being refused.
 *  - **it shows what each answer cost.** Under every reply, in their own
 *    currency. A bill at the end of the month that nobody can reconcile to
 *    anything they remember doing is how usage-based pricing loses trust.
 *
 * The conversation lives in component state only. It is not persisted, and the
 * history is capped, because every prior turn is re-sent and re-billed on the
 * next question — an unbounded transcript is an unbounded bill.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Send, AlertTriangle, Lock } from 'lucide-react';
import type { AiChatResponse, AiTierView } from '@cenox/shared';
import { useAuth } from '../../lib/auth';
import { ApiError, api } from '../../lib/api';
import { Badge, Button, Card, CardHeader, EmptyState, Spinner } from '../../components/ui';

interface Turn {
  role: 'user' | 'assistant';
  content: string;
  /** Present on answers: what this one cost, in the business's currency. */
  cost?: { amount: string; currencyCode: string | null; label: string; replayed: boolean };
}

/**
 * How many prior turns travel with the next question.
 *
 * Ten is a judgement, not a limit of the API: enough that the assistant
 * follows a conversation, few enough that the twentieth question does not
 * cost five times the first. The server caps it at twenty regardless.
 */
const HISTORY_TURNS = 10;

const money = (amount: string, currency: string | null): string => {
  const n = Number(amount);
  // Sub-cent amounts are the common case for a short question, and rounding
  // them to "$0.00" would read as free.
  const shown = n > 0 && n < 0.01 ? n.toFixed(4) : n.toFixed(2);
  return `${currency ?? ''} ${shown}`.trim();
};

export function AssistantPage(): ReactNode {
  const { business, can } = useAuth();
  const queryClient = useQueryClient();
  const path = (suffix: string) => `/businesses/${business?.businessId}${suffix}`;

  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [tier, setTier] = useState<AiTierView['tier']>('default');
  const [error, setError] = useState<string | null>(null);

  const transcriptEnd = useRef<HTMLDivElement>(null);

  const tiers = useQuery({
    queryKey: ['ai-tiers', business?.businessId],
    queryFn: () => api.get<{ data: AiTierView[] }>(path('/ai/tiers')),
    enabled: Boolean(business),
  });

  useEffect(() => {
    transcriptEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns]);

  /**
   * One key per question, generated before the request and reused if it is
   * retried. Without it, a network timeout and a genuine second question look
   * identical to the server — and the customer pays for both.
   */
  const idempotencyKey = useRef<string | null>(null);

  const ask = useMutation({
    mutationFn: (message: string) => {
      idempotencyKey.current ??= crypto.randomUUID();

      return api.post<AiChatResponse>(path('/ai/chat'), {
        message,
        history: turns.slice(-HISTORY_TURNS).map((t) => ({ role: t.role, content: t.content })),
        ...(tier === 'default'
          ? {}
          : { tier, escalationReason: 'Requested from the assistant screen' }),
        idempotencyKey: idempotencyKey.current,
      });
    },
    onSuccess: (result) => {
      idempotencyKey.current = null;
      setError(null);
      setTurns((prior) => [
        ...prior,
        {
          role: 'assistant',
          content: result.text,
          cost: {
            amount: result.amountCharged,
            currencyCode: result.currencyCode,
            label: result.label,
            replayed: result.replayed,
          },
        },
      ]);
      // The AI allowance moved, so anything showing it is now stale.
      void queryClient.invalidateQueries({ queryKey: ['usage'] });
    },
    onError: (caught) => {
      // The key is deliberately NOT cleared: if the user tries again, the same
      // key goes out, and a request the provider actually completed is
      // replayed rather than charged twice.
      setError(caught instanceof ApiError ? caught.message : 'Could not reach the assistant.');
    },
  });

  const canAsk = can('ai:use');
  const available = tiers.data?.data ?? [];
  const selected = available.find((t) => t.tier === tier);

  const submit = (): void => {
    const message = draft.trim();
    if (!message || ask.isPending || !canAsk) return;

    setTurns((prior) => [...prior, { role: 'user', content: message }]);
    setDraft('');
    ask.mutate(message);
  };

  if (tiers.isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Assistant</h1>
        <p className="text-sm text-slate-500">
          Ask about your business. CenoX-managed answers are charged by usage against your AI
          credit; with your own OpenAI key, OpenAI bills you directly.
        </p>
      </div>

      <Card>
        <CardHeader
          title="Ask CenoX AI"
          subtitle={selected ? `${selected.label} · charged by usage` : 'Choose how much thinking power to use'}
          action={
            <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
              {available.map((option) => (
                <button
                  key={option.tier}
                  type="button"
                  disabled={!option.available}
                  onClick={() => setTier(option.tier)}
                  // A tier the plan excludes stays visible but disabled: hiding
                  // it means nobody learns it exists, and enabling it means
                  // they learn by being refused.
                  title={
                    option.available ? 'Charged by usage' : 'Not included in your plan'
                  }
                  className={`flex items-center gap-1 rounded-md px-3 py-1 text-sm ${
                    option.tier === tier
                      ? 'bg-indigo-700 text-white'
                      : option.available
                        ? 'text-slate-600 hover:text-slate-900'
                        : 'cursor-not-allowed text-slate-300'
                  }`}
                >
                  {!option.available && <Lock className="h-3 w-3" />}
                  {option.label.replace('CenoX AI', '').replace(/[()]/g, '').trim() || 'Standard'}
                </button>
              ))}
            </div>
          }
        />

        <div className="max-h-[28rem] space-y-3 overflow-y-auto p-4 pt-0">
          {turns.length === 0 && (
            <EmptyState
              icon={<Sparkles className="h-6 w-6" />}
              title="Nothing asked yet"
              description="Try: what were my best-selling products last week?"
            />
          )}

          {turns.map((turn, index) => (
            <div
              key={index}
              className={turn.role === 'user' ? 'flex justify-end' : 'flex justify-start'}
            >
              <div
                className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                  turn.role === 'user'
                    ? 'bg-indigo-700 text-white'
                    : 'border border-slate-200 bg-white text-slate-800'
                }`}
              >
                <p className="whitespace-pre-wrap">{turn.content}</p>

                {turn.cost && (
                  <p className="mt-1.5 flex items-center gap-2 text-xs text-slate-400">
                    <span>{turn.cost.label}</span>
                    <span>·</span>
                    {/* What this answer cost, next to the answer. A monthly
                        bill nobody can reconcile is how metered pricing
                        loses trust. */}
                    <span className="tabular-nums">
                      {money(turn.cost.amount, turn.cost.currencyCode)}
                    </span>
                    {turn.cost.replayed && <Badge tone="info">Retried — not charged again</Badge>}
                  </p>
                )}
              </div>
            </div>
          ))}

          {ask.isPending && (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Spinner /> Thinking…
            </div>
          )}

          <div ref={transcriptEnd} />
        </div>

        {error && (
          <div className="mx-4 mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <div className="flex gap-2 border-t border-slate-100 p-4">
          <textarea
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              // Enter sends, Shift+Enter breaks the line — what every chat
              // does, and what fingers expect.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            rows={2}
            disabled={!canAsk}
            placeholder={
              canAsk ? 'Ask about your sales, stock or customers…' : 'You do not have AI access'
            }
            className="flex-1 resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none disabled:bg-slate-50"
          />
          <Button onClick={submit} disabled={!canAsk || ask.isPending || !draft.trim()}>
            <Send className="h-4 w-4" />
            Ask
          </Button>
        </div>
      </Card>
    </div>
  );
}
